import { useId, useMemo, type CSSProperties } from 'react';
import { useResolvedTheme } from '@/theme/theme';
import { ART_PAL, type ArtTheme } from './covers/kit';
import { rasterArt } from './raster';
import { rocketMarkup } from './rocketArt';
import s from './rocket.module.css';

/** The raster rocket (ADDENDUM: `crash/rocket`) flies 45° up-right; turned to face right like the vector one. */
const RASTER_HEADING = 45;
/*
 * `crash/rocket-crashed` (Codex task 4) is drawn falling ahead — nose down-right, smoke trailing up-left — so it is
 * used as is (no mirror, no turn); Chart.tsx tilts and sinks the whole plane on the crash.
 */

/**
 * The Liftoff rocket (faces right, 128×128 art scaled to `size`). `crashed` → tilted nose-down with a small puff.
 * Decorative (aria-hidden). Gradient ids are unique per instance (useId). Raster (`crash/rocket` +
 * `crash/rocket-crashed`, only as a pair) replaces the vector once `pnpm art:process` has produced it.
 */
export function Rocket({
  crashed = false,
  size = 64,
  className,
  style,
  theme,
}: {
  crashed?: boolean;
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  theme?: ArtTheme;
}) {
  const current = useResolvedTheme();
  const t = theme ?? current;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const raster = rasterArt('crash', crashed ? 'rocket-crashed' : 'rocket');
  const html = useMemo(
    () => (raster ? '' : rocketMarkup(ART_PAL[t], `rk${uid}`, crashed)),
    [raster, t, uid, crashed],
  );
  if (raster)
    return (
      <img
        src={raster}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={className ? `${s.rocket} ${className}` : s.rocket}
        style={{
          width: size,
          height: size,
          ...(crashed ? null : { rotate: `${RASTER_HEADING}deg` }),
          ...style,
        }}
        data-crashed={crashed || undefined}
        data-raster=""
      />
    );
  return (
    <svg
      viewBox="0 0 128 128"
      width={size}
      height={size}
      className={className ? `${s.rocket} ${className}` : s.rocket}
      style={style}
      aria-hidden="true"
      focusable="false"
      data-crashed={crashed || undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
